
import React from 'react';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  variant?: 'default' | 'destructive';
  duration?: number;
}

export interface ToastProps {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
  duration?: number;
  action?: React.ReactNode;
}

const TOAST_TIMEOUT = 5000;

const useToast = () => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  React.useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    toasts.forEach((toast) => {
      if (toast.duration !== Infinity) {
        const timer = setTimeout(() => {
          setToasts((prevToasts) => prevToasts.filter((t) => t.id !== toast.id));
        }, toast.duration || TOAST_TIMEOUT);
        
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [toasts]);

  const toast = ({ title, description, variant = "default", duration = TOAST_TIMEOUT, action }: ToastProps) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = { id, title, description, variant, duration, action };
    
    setToasts((prevToasts) => [...prevToasts, newToast]);
    
    return {
      id,
      dismiss: () => setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id)),
    };
  };

  const dismiss = (toastId: string) => {
    setToasts((prevToasts) => prevToasts.filter((t) => t.id !== toastId));
  };

  return {
    toast,
    dismiss,
    toasts
  };
};

// Create a singleton instance for direct imports
let toasts: Toast[] = [];
let listeners: Function[] = [];

const subscribe = (listener: Function) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

const notify = () => {
  listeners.forEach((listener) => listener(toasts));
};

export const toast = ({ title, description, variant = "default", duration = TOAST_TIMEOUT, action }: ToastProps) => {
  const id = Math.random().toString(36).substring(2, 9);
  const newToast = { id, title, description, variant, duration, action };
  
  toasts = [...toasts, newToast];
  notify();
  
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, duration);
  
  return {
    id,
    dismiss: () => {
      toasts = toasts.filter((t) => t.id !== id);
      notify();
    },
  };
};

export { useToast };
